/**
 * WebSocket Service для работы с уведомлениями в реальном времени
 * Подключается к бэкенду через WebSocket и получает события
 */

import { isTokenExpired } from "../utils/jwt";

// Global callback for order refresh - will be set by useOrderNotifications hook
let globalOrderRefreshCallback: (() => void) | null = null;

export const setOrderRefreshCallback = (callback: () => void) => {
  globalOrderRefreshCallback = callback;
};

class WebSocketNotificationService {
  private ws: WebSocket | null = null;
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 10;
  private reconnectDelay = 5000; // 5 секунд (увеличено)
  private heartbeatInterval: number | null = null;
  private url: string = "";
  private reconnectTimer: number | null = null;
  private isManualDisconnect = false;
  private isConnecting = false;
  private currentToken: string | null = null;

  // Callbacks для событий
  private onOrderUpdateCallback: ((data: any) => void) | null = null;
  private onStatusChangeCallback: ((data: any) => void) | null = null;
  private onNewNotificationCallback: ((data: any) => void) | null = null;
  private onErrorCallback: ((error: any) => void) | null = null;
  private onConnectCallback: (() => void) | null = null;
  private onDisconnectCallback: (() => void) | null = null;

  // Order-specific callbacks
  private orderUpdateCallbacks: Map<number, Array<(data: any) => void>> =
    new Map();
  private globalOrderUpdateCallback: ((data: any) => void) | null = null;

  /**
   * Проверка истечения JWT токена
   */
  private checkTokenExpired(token: string): boolean {
    return isTokenExpired(token);
  }

  /**
   * Подключение к WebSocket серверу
   * @param baseUrl - URL бэкенда (например, ws://localhost:2609)
   * @param token - JWT токен для авторизации
   */
  connect(baseUrl: string, token: string): void {
    // Проверяем валидность токена перед подключением
    if (this.checkTokenExpired(token)) {
      console.warn(
        "[WebSocket] Token expired, not connecting. Triggering token refresh...",
      );
      window.dispatchEvent(new CustomEvent("auth-token-expired"));
      return;
    }

    // Если уже подключены - не подключаемся
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      // Проверяем, не изменился ли токен
      if (this.currentToken === token) {
        return;
      }
      this.disconnect();
    }

    // Если идет процесс подключения - не подключаемся
    if (
      this.isConnecting ||
      (this.ws && this.ws.readyState === WebSocket.CONNECTING)
    ) {
      return;
    }

    // Сбрасываем флаг ручного отключения при новом подключении
    this.isManualDisconnect = false;
    this.isConnecting = true;
    this.currentToken = token;

    // Формируем URL для WebSocket - используем window.location для правильного хоста
    const protocol = window.location.protocol === "https:" ? "wss:" : "ws:";
    const wsUrl = `${protocol}//${window.location.hostname}${window.location.port ? ":" + window.location.port : ""}/api/v1/ws/notifications?token=${token}`;

    this.url = wsUrl;

    try {
      this.ws = new WebSocket(wsUrl);

      this.ws.onopen = () => {
        this.isConnecting = false;
        this.reconnectAttempts = 0;
        this.isManualDisconnect = false;
        this.startHeartbeat();
        this.onConnectCallback?.();
      };

      this.ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          this.handleMessage(data);
        } catch (error) {
          console.error("[WebSocket] Error parsing message:", error);
        }
      };

      this.ws.onerror = (error) => {
        console.error("[WebSocket] Error:", error);
        this.isConnecting = false;
        this.onErrorCallback?.(error);
      };

      this.ws.onclose = (event) => {
        this.isConnecting = false;
        this.stopHeartbeat();
        this.onDisconnectCallback?.();

        // Обработка 401 ошибки (невалидный токен)
        if (
          event.code === 4001 ||
          (event.reason && event.reason.includes("401"))
        ) {
          console.warn(
            "[WebSocket] Authentication failed, triggering token refresh...",
          );
          window.dispatchEvent(new CustomEvent("auth-token-expired"));
          this.isManualDisconnect = true; // Не переподключаемся без нового токена
          return;
        }

        // Автоматическое переподключение ТОЛЬКО если не было ручной дисконнект
        if (
          !this.isManualDisconnect &&
          this.reconnectAttempts < this.maxReconnectAttempts
        ) {
          this.scheduleReconnect();
        }
      };
    } catch (error) {
      console.error("[WebSocket] Connection error:", error);
      this.isConnecting = false;
      this.onErrorCallback?.(error);
      if (!this.isManualDisconnect) {
        this.scheduleReconnect();
      }
    }
  }

  /**
   * Обработка входящих сообщений
   */
  private handleMessage(data: any): void {
    const { type, payload } = data;

    switch (type) {
      case "order_update":
        this.onOrderUpdateCallback?.(payload);
        // Call global order update callback
        this.globalOrderUpdateCallback?.(payload);
        // Trigger order refresh for UI updates
        if (globalOrderRefreshCallback) {
          globalOrderRefreshCallback();
        }
        // Call specific order callbacks
        const orderId = payload.order_id || payload.id;
        if (orderId && this.orderUpdateCallbacks.has(orderId)) {
          this.orderUpdateCallbacks.get(orderId)?.forEach((cb) => cb(payload));
        }
        break;
      case "status_change":
        this.onStatusChangeCallback?.(payload);
        // Also trigger order update for status changes
        this.globalOrderUpdateCallback?.(payload);
        // Trigger order refresh for UI updates
        if (globalOrderRefreshCallback) {
          globalOrderRefreshCallback();
        }
        const statusOrderId = payload.order_id || payload.id;
        if (statusOrderId && this.orderUpdateCallbacks.has(statusOrderId)) {
          this.orderUpdateCallbacks
            .get(statusOrderId)
            ?.forEach((cb) => cb(payload));
        }
        break;
      case "new_notification":
        this.onNewNotificationCallback?.(payload);
        break;
      case "order_notification":
      case "general_notification":
      case "enhanced_notification":
        // Handle backend notification types - convert to new_notification format
        const transformedNotification = {
          id: payload.order?.id || payload.data?.id || Date.now(),
          title: payload.title,
          body: payload.message,
          type: payload.type || 'system',
          order_id: payload.order?.id || payload.data?.order_id,
          created_at: new Date().toISOString(),
        };
        this.onNewNotificationCallback?.(transformedNotification);
        // Also notify order update subscribers for status changes
        this.onOrderUpdateCallback?.(payload);
        // Trigger order refresh for UI updates
        if (globalOrderRefreshCallback) {
          globalOrderRefreshCallback();
        }
        // Also trigger global order update callbacks
        this.globalOrderUpdateCallback?.(payload);
        const notifOrderId = payload.order?.id || payload.data?.order_id;
        if (notifOrderId && this.orderUpdateCallbacks.has(notifOrderId)) {
          this.orderUpdateCallbacks.get(notifOrderId)?.forEach((cb) => cb(payload));
        }
        break;
      case "performer_responded":
        this.onNewNotificationCallback?.(payload);
        // Trigger global order refresh to update ResponsesPanel and other components
        if (globalOrderRefreshCallback) {
          globalOrderRefreshCallback();
        }
        // Also trigger order update to refresh the order data
        const responseOrderId = payload.order || payload.order_id;
        if (responseOrderId && this.orderUpdateCallbacks.has(responseOrderId)) {
          this.orderUpdateCallbacks
            .get(responseOrderId)
            ?.forEach((cb) => cb(payload));
        }
        break;
      case "ping":
        // Ответ на ping
        this.sendPong();
        break;
      default:
    }
  }

  /**
   * Отправка pong в ответ на ping от сервера
   */
  private sendPong(): void {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify({ type: "pong" }));
    }
  }

  /**
   * Запуск heartbeat для поддержания соединения
   */
  private startHeartbeat(): void {
    this.heartbeatInterval = window.setInterval(() => {
      if (this.ws && this.ws.readyState === WebSocket.OPEN) {
        this.ws.send(JSON.stringify({ type: "ping" }));
      }
    }, 30000); // Каждые 30 секунд
  }

  /**
   * Остановка heartbeat
   */
  private stopHeartbeat(): void {
    if (this.heartbeatInterval) {
      clearInterval(this.heartbeatInterval);
      this.heartbeatInterval = null;
    }
  }

  /**
   * Планирование переподключения
   */
  private scheduleReconnect(): void {
    this.reconnectAttempts++;
    const delay = Math.min(
      this.reconnectDelay * Math.pow(2, this.reconnectAttempts - 1),
      60000,
    ); // Exponential backoff, max 60s
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
    }

    this.reconnectTimer = window.setTimeout(() => {
      if (!this.isManualDisconnect) {
        this.connectFromUrl();
      }
    }, delay);
  }

  /**
   * Подключение с использованием сохраненного URL
   */
  private connectFromUrl(): void {
    if (this.url) {
      const token = this.extractTokenFromUrl(this.url);
      const baseUrl = this.extractBaseUrl(this.url);
      if (token && baseUrl) {
        this.connect(baseUrl, token);
      }
    }
  }

  /**
   * Извлечение токена из URL
   */
  private extractTokenFromUrl(url: string): string | null {
    const match = url.match(/[?&]token=([^&]+)/);
    return match ? match[1] : null;
  }

  /**
   * Извлечение baseUrl из URL
   */
  private extractBaseUrl(url: string): string | null {
    const match = url.match(/^(wss?:\/\/[^/]+)(\/.*)?$/);
    return match ? match[1].replace("ws", "http") : null;
  }

  /**
   * Ручное отключение (без переподключения)
   */
  public manualDisconnect(): void {
    this.isManualDisconnect = true;
    this.reconnectAttempts = this.maxReconnectAttempts; // Prevent reconnect

    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }

    if (this.ws) {
      this.ws.close(1000, "Manual disconnect");
      this.ws = null;
    }
    this.currentToken = null;
    this.stopHeartbeat();
  }

  /**
   * Отключение от WebSocket
   */
  disconnect(): void {
    this.isConnecting = false;
    this.stopHeartbeat();

    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }

    if (this.ws) {
      this.ws.close(1000, "Client disconnect");
      this.ws = null;
    }
    this.currentToken = null;
    // НЕ устанавливаем isManualDisconnect = true здесь
    // Это должен делать только manualDisconnect()
  }

  /**
   * Отправка сообщения на сервер
   */
  send(type: string, payload: any): void {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify({ type, payload }));
    } else {
      console.warn("[WebSocket] Cannot send message - not connected");
    }
  }

  /**
   * Установка callback для обновлений заказов
   */
  onOrderUpdate(callback: (data: any) => void): void {
    this.onOrderUpdateCallback = callback;
  }

  /**
   * Установка callback для изменений статуса
   */
  onStatusChange(callback: (data: any) => void): void {
    this.onStatusChangeCallback = callback;
  }

  /**
   * Установка callback для новых уведомлений
   */
  onNewNotification(callback: (data: any) => void): void {
    this.onNewNotificationCallback = callback;
  }

  /**
   * Установка callback для ошибок
   */
  onError(callback: (error: any) => void): void {
    this.onErrorCallback = callback;
  }

  /**
   * Установка callback для подключения
   */
  onConnect(callback: () => void): void {
    this.onConnectCallback = callback;
  }

  /**
   * Установка callback для отключения
   */
  onDisconnect(callback: () => void): void {
    this.onDisconnectCallback = callback;
  }

  /**
   * Проверка состояния подключения
   */
  isConnected(): boolean {
    return this.ws !== null && this.ws.readyState === WebSocket.OPEN;
  }

  /**
   * Получение состояния подключения
   */
  getReadyState(): number {
    return this.ws ? this.ws.readyState : WebSocket.CLOSED;
  }

  /**
   * Subscribe to order updates for a specific order
   * @param orderId - ID заказа для подписки
   * @param callback - Callback функция для получения обновлений
   * @returns Функция для отписки
   */
  subscribeToOrderUpdates(callback: (data: any) => void): () => void {
    this.globalOrderUpdateCallback = callback;

    return () => {
      this.globalOrderUpdateCallback = null;
    };
  }

  /**
   * Subscribe to updates for a specific order ID
   * @param orderId - ID заказа
   * @param callback - Callback функция
   * @returns Функция для отписки
   */
  subscribeToOrder(orderId: number, callback: (data: any) => void): () => void {
    if (!this.orderUpdateCallbacks.has(orderId)) {
      this.orderUpdateCallbacks.set(orderId, []);
    }
    this.orderUpdateCallbacks.get(orderId)!.push(callback);

    return () => {
      const callbacks = this.orderUpdateCallbacks.get(orderId);
      if (callbacks) {
        const index = callbacks.indexOf(callback);
        if (index > -1) {
          callbacks.splice(index, 1);
        }
      }
    };
  }

  // --- Глобальное управление WebSocket ---

  private static isGlobalInitialized = false;

  /**
   * Инициализация глобального WebSocket соединения
   */
  static initializeGlobal(token: string): void {
    if (WebSocketNotificationService.isGlobalInitialized) {
      return;
    }

    try {
      const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
      const baseUrl = `${protocol}//${window.location.hostname}${window.location.port ? ':' + window.location.port : ''}/api/v1`;
      const instance = websocketNotificationService;

      instance.connect(baseUrl, token);
      WebSocketNotificationService.isGlobalInitialized = true;
    } catch (error) {
      console.error('[WebSocket] Global initialization error:', error);
    }
  }

  /**
   * Обновить глобальное WebSocket соединение с новым токеном
   */
  static refreshGlobal(token: string): void {
    if (!WebSocketNotificationService.isGlobalInitialized) {
      WebSocketNotificationService.initializeGlobal(token);
      return;
    }

    try {
      const instance = websocketNotificationService;
      instance.disconnect();

      setTimeout(() => {
        const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
        const baseUrl = `${protocol}//${window.location.hostname}${window.location.port ? ':' + window.location.port : ''}/api/v1`;
        instance.connect(baseUrl, token);
      }, 500);
    } catch (error) {
      console.error('[WebSocket] Global refresh error:', error);
    }
  }

  /**
   * Отключить глобальное WebSocket соединение
   */
  static disconnectGlobal(): void {
    if (!WebSocketNotificationService.isGlobalInitialized) return;

    try {
      websocketNotificationService.manualDisconnect();
      WebSocketNotificationService.isGlobalInitialized = false;
    } catch (error) {
      console.error('[WebSocket] Global disconnect error:', error);
    }
  }

  /**
   * Проверить глобальное состояние WebSocket
   */
  static isGloballyConnected(): boolean {
    return websocketNotificationService.isConnected();
  }

  // --- Instance convenience methods (delegate to static) ---

  initializeGlobal(token: string): void {
    WebSocketNotificationService.initializeGlobal(token);
  }

  refreshGlobal(token: string): void {
    WebSocketNotificationService.refreshGlobal(token);
  }

  disconnectGlobal(): void {
    WebSocketNotificationService.disconnectGlobal();
  }
}

// Экспортируем singleton instance
export const websocketNotificationService = new WebSocketNotificationService();
export default websocketNotificationService;
